import { Link } from "react-router-dom";
import {
  Users,
  Mail,
  MousePointerClick,
  KeyRound,
  Clock3,
  ShieldCheck,
  Lock,
  Eye,
  MessageCircle,
  ShoppingBag,
  Package,
  BarChart3,
  Crown,
  Sparkles,
  ArrowUpRight,
  BookOpen,
  CheckCircle2,
  XCircle,
  Calendar,
  Percent,
  Bell,
} from "lucide-react";
import SEO from "@/components/SEO";
import DashboardHeader from "@/components/DashboardHeader";
import DashboardFooter from "@/components/DashboardFooter";
import { useOptionalMerchant, isOwner } from "@/lib/useMerchant";
import BackToDashboard from "@/components/BackToDashboard";

/**
 * TeamGuide.jsx
 * ------------------------------------------------------------------
 * Companion to TabsGuide.jsx / StatusGuide.jsx, but for the Team feature
 * specifically. Deliberately NOT wrapped in RequireOwner — both owners
 * (deciding whether to invite someone) and members (wondering what
 * they're allowed to do) have a reason to land here, and the content
 * itself never exposes anything sensitive either way.
 * ------------------------------------------------------------------
 */

const ACCENTS = {
  lilac: { chip: "bg-lilac", chipText: "text-ink", soft: "bg-lilac/25" },
  mint: { chip: "bg-mint", chipText: "text-ink", soft: "bg-mint/25" },
  peach: { chip: "bg-peach", chipText: "text-ink", soft: "bg-peach/25" },
  hotpink: { chip: "bg-hotpink", chipText: "text-white", soft: "bg-hotpink/10" },
  ink: { chip: "bg-ink", chipText: "text-cream", soft: "bg-ink/[0.06]" },
};

function StepCard({ n, icon: Icon, accent, title, desc, note }) {
  const a = ACCENTS[accent];
  return (
    <div className="relative rounded-2xl border border-ink/10 bg-white p-5">
      <div className="flex items-start gap-3.5">
        <span className={`grid h-10 w-10 flex-none place-items-center rounded-2xl ${a.chip} ${a.chipText} font-display text-sm font-black`}>
          {n}
        </span>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon size={15} className="text-ink/50" />
            <p className="font-bold text-ink">{title}</p>
          </div>
          <p className="mt-1.5 text-[13px] leading-relaxed text-ink/70">{desc}</p>
          {note && <p className="mt-2 text-[12px] leading-relaxed text-ink/45">{note}</p>}
        </div>
      </div>
    </div>
  );
}

function PermissionRow({ icon: Icon, label, allowed }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 odd:bg-ink/[0.02]">
      <div className="flex items-center gap-2.5 text-sm text-ink/80">
        <Icon size={14} className="flex-none text-ink/40" />
        {label}
      </div>
      {allowed ? (
        <span className="inline-flex flex-none items-center gap-1 rounded-full bg-mint/60 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-ink">
          <CheckCircle2 size={11} /> Yes
        </span>
      ) : (
        <span className="inline-flex flex-none items-center gap-1 rounded-full bg-ink/[0.06] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-ink/50">
          <XCircle size={11} /> Owner only
        </span>
      )}
    </div>
  );
}

const MEMBER_PERMISSIONS = [
  { icon: MessageCircle, label: "View & reply in Chats", allowed: true },
  { icon: Users, label: "View & edit Customers", allowed: true },
  { icon: ShoppingBag, label: "Confirm/reject payments, log returns in Orders & Returns", allowed: true },
  { icon: Package, label: "View & manage Catalog", allowed: true },
  { icon: BarChart3, label: "View Analytics", allowed: true },
  { icon: Users, label: "Invite, remove, or view the Team page", allowed: false },
  { icon: Sparkles, label: "Create/edit Automations & Comment-to-DM rules", allowed: false },
  { icon: KeyRound, label: "Change discount codes or the shipping fee", allowed: false },
  { icon: ShieldCheck, label: "Business Settings — bank details, UPI, Razorpay keys, Instagram connection", allowed: false },
  { icon: Crown, label: "Billing, plan changes, or payment history", allowed: false },
];

export default function TeamGuide() {
  const { me } = useOptionalMerchant();
  const owner = isOwner(me);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: "Team Access Guide",
    description: "How inviting teammates works, what member access looks like, and what team members can and can't do.",
    publisher: { "@type": "Organization", name: "Checkout DM", url: "https://checkoutdm.com" },
    mainEntityOfPage: "https://checkoutdm.com/team-guide",
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://checkoutdm.com/" },
      { "@type": "ListItem", position: 2, name: "Team Access Guide", item: "https://checkoutdm.com/team-guide" },
    ],
  };

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Team Access Guide | Checkout DM"
        description="How inviting teammates works, what member access looks like, and what team members can and can't do."
        path="/team-guide"
        structuredData={[articleSchema, breadcrumbSchema]}
      />
      <DashboardHeader me={me} />

      <main className="mx-auto max-w-4xl px-6 pt-10 md:px-10">
        {me && <BackToDashboard />}

        <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Help</p>
        <h1 className="mt-2 font-display text-4xl font-black tracking-tighter md:text-5xl">
          How Team access works
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ink/70 md:text-base">
          {owner
            ? "You're the owner — this walks through inviting teammates, how their access works, and what they can and can't do."
            : "You're logged in as a team member — this explains how you got access, and exactly what you can and can't do here."}
        </p>
        <p className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm">
          <Link to="/tabs-guide" className="inline-flex items-center gap-1.5 font-semibold text-hotpink hover:underline">
            <BookOpen size={13} /> What each dashboard tab means →
          </Link>
        </p>

        {/* How it works */}
        <section className="mt-8 rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8">
          <div className="flex items-center gap-3.5">
            <span className="grid h-12 w-12 flex-none place-items-center rounded-2xl bg-lilac text-ink">
              <Users size={20} strokeWidth={2.2} />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-ink/50">From invite to logged in</p>
              <h2 className="mt-0.5 font-display text-2xl font-black tracking-tight md:text-3xl">
                Three steps
              </h2>
            </div>
          </div>

          <div className="mt-6 grid gap-3 md:grid-cols-2">
            <StepCard
              n={1}
              icon={Mail}
              accent="lilac"
              title="Owner sends the invite"
              desc={
                owner
                  ? "From the Team page, enter a name and email and send the invite. They'll get an email with an Accept link."
                  : "The business owner enters your name and email on their Team page. You'll get an email with an Accept link."
              }
              note="The invite link is valid for 48 hours. If it expires before it's accepted, the owner needs to remove the pending invite and send a fresh one — the same email can be re-invited right away."
            />
            <StepCard
              n={2}
              icon={MousePointerClick}
              accent="mint"
              title="Member accepts"
              desc="Clicking Accept in the email logs the new member straight into the dashboard — no separate login step needed right away. That first session lasts 24 hours."
            />
            <StepCard
              n={3}
              icon={KeyRound}
              accent="peach"
              title="Logging in after that"
              desc='Once that first session expires, members log back in from /team/login: enter the email the invite was sent to, and a one-time login link is emailed over.'
              note="The login link works once and expires after 1 hour, same as a password-reset link. There's no password to remember — every login goes through a fresh emailed link."
            />
          </div>
        </section>

        {/* Permissions */}
        <section className="mt-6 rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8">
          <div className="flex items-center gap-3.5">
            <span className="grid h-12 w-12 flex-none place-items-center rounded-2xl bg-hotpink text-white">
              <Eye size={20} strokeWidth={2.2} />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">
                {owner ? "What members can access" : "What you can access"}
              </p>
              <h2 className="mt-0.5 font-display text-2xl font-black tracking-tight md:text-3xl">
                One role today: Member
              </h2>
            </div>
          </div>

          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ink/70">
            Every invite joins with the same permissions — there's one Owner (whoever connected the
            Instagram account) and everyone else is a Member. Members handle the day-to-day: replying
            to customers, confirming payments, logging returns, keeping the catalog current. Anything
            that touches money, settings, or who else has access stays with the owner.
          </p>

          <div className="mt-5 divide-y divide-ink/5 overflow-hidden rounded-2xl border border-ink/10">
            {MEMBER_PERMISSIONS.map((p) => (
              <PermissionRow key={p.label} {...p} />
            ))}
          </div>

          <p className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-ink/45">
            <Lock size={13} className="mt-0.5 flex-none" />
            These aren't just hidden in the menu — the server checks and blocks owner-only actions
            directly, so even a direct link to one of those pages redirects back to the dashboard.
          </p>
        </section>

        {/* Seats */}
        <section className="mt-6 rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8">
          <div className="flex items-center gap-3.5">
            <span className="grid h-12 w-12 flex-none place-items-center rounded-2xl bg-mint text-ink">
              <Sparkles size={20} strokeWidth={2.2} />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-ink/50">Growing the team</p>
              <h2 className="mt-0.5 font-display text-2xl font-black tracking-tight md:text-3xl">
                2 seats included, more if you need them
              </h2>
            </div>
          </div>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ink/70">
            Team is a Pro-plan feature, and Pro includes 2 member seats. Need a third pair of hands?
            {owner ? " From the Team page, " : " The owner can, from the Team page, "}
            add an extra seat for a small monthly add-on charged via Razorpay.
          </p>

          <div className="mt-6 grid gap-3 md:grid-cols-3">
            <StepCard
              n={1}
              icon={Calendar}
              accent="peach"
              title="One shared expiry"
              desc="Every extra seat you buy expires on the same date — buying a 2nd or 3rd extra seat doesn't start a separate 30-day clock of its own."
            />
            <StepCard
              n={2}
              icon={Percent}
              accent="lilac"
              title="Prorated pricing"
              desc="Buying an extra seat while another is still active only charges for the days actually left until the shared expiry date — never a full month again for that seat."
            />
            <StepCard
              n={3}
              icon={ShieldCheck}
              accent="mint"
              title="You always see the date first"
              desc="Before any payment goes through, you're shown the exact price and the exact date your seat(s) will expire — no surprises."
            />
          </div>

          <div className="mt-4 flex items-start gap-3 rounded-2xl border border-ink/10 bg-ink/[0.02] p-5">
            <span className="grid h-9 w-9 flex-none place-items-center rounded-full bg-white text-hotpink">
              <Bell className="h-4 w-4" />
            </span>
            <div className="text-sm leading-relaxed text-ink/70">
              <p>
                The owner gets a reminder email 1–3 days before extra seats expire. If they're not
                renewed by then, the most recently added member(s) are automatically signed out and
                the extra seat(s) removed — everyone else on the team, including the owner, is
                unaffected.
              </p>
              <p className="mt-2">
                To keep seats going, just buy again from the Team page — any time before or after
                they lapse.
              </p>
            </div>
          </div>

          {owner && (
            <Link
              to="/team"
              className="cta-glow mt-5 inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-5 py-2.5 text-sm font-semibold text-white"
            >
              Go to Team page <ArrowUpRight size={14} />
            </Link>
          )}
        </section>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-[2rem] border border-hotpink/20 bg-hotpink/5 p-6 md:p-8">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 flex-none place-items-center rounded-2xl bg-hotpink/10 text-hotpink">
              <Clock3 size={17} />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Still stuck?</p>
              <p className="mt-1 max-w-md text-sm text-ink/70">
                If something about your access still doesn't make sense, ask the person who invited
                you — or if that's you, reach out and we'll add it here for the next person too.
              </p>
            </div>
          </div>
        </div>
      </main>

      <div className="mt-10">
        <DashboardFooter />
      </div>
    </div>
  );
}
